import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Phone, User, Calendar, FileText, DollarSign, Truck, Clock, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import AdminLayout from './AdminLayout';
import { getOrderById, updateOrderStatus, isAuthenticated } from '../../services/adminApi';

const statusColors = {
  NEW: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  CONFIRMED: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  PAID: 'bg-green-500/20 text-green-300 border-green-500/30',
  CANCELLED: 'bg-red-500/20 text-red-300 border-red-500/30',
  DELIVERED: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
};

const AdminOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    fetchOrder();
  }, [orderId, navigate]);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const data = await getOrderById(orderId);
      setOrder(data);
    } catch (error) {
      console.error('Failed to fetch order:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setUpdating(true);
    try {
      const updated = await updateOrderStatus(orderId, newStatus);
      setOrder(updated);
    } catch (error) {
      console.error('Failed to update status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <RefreshCw className="w-8 h-8 text-stone-500 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!order) {
    return (
      <AdminLayout>
        <div className="p-4 md:p-8">
          <Button onClick={() => navigate('/admin/orders')} variant="ghost" className="text-stone-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-stone-600 mx-auto mb-4" />
            <p className="text-stone-400">Order not found</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <Button
              onClick={() => navigate('/admin/orders')}
              variant="ghost"
              className="text-stone-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="font-serif text-2xl font-semibold text-white">Order Details</h1>
              <p className="text-stone-500 font-mono text-sm">{order.id}</p>
            </div>
          </div>
          <Badge className={`${statusColors[order.status]} border text-sm px-3 py-1`}>
            {order.status}
          </Badge>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="xl:col-span-2 space-y-6">
            {/* Bouquet Details */}
            <Card className="bg-[#2a2a2e] border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-rose-400" />
                  Bouquet Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-stone-500 text-sm">Rose Variety</p>
                    <p className="text-white font-medium">{order.roseVariety}</p>
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Stem Length</p>
                    <p className="text-white font-medium">{order.stemLength} cm</p>
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Quantity</p>
                    <p className="text-white font-medium">{order.quantity} stems</p>
                  </div>
                  <div>
                    <p className="text-stone-500 text-sm">Wrap</p>
                    <p className="text-white font-medium">
                      {order.wrapOption === 'PREMIUM_WRAP' ? 'Premium Wrap' : 'Ribbon Bow'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Customer Info */}
            <Card className="bg-[#2a2a2e] border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-rose-400" />
                  Customer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <User className="w-4 h-4 text-stone-500 mt-1" />
                    <div>
                      <p className="text-stone-500 text-sm">Name</p>
                      <p className="text-white">{order.customerName || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Phone className="w-4 h-4 text-stone-500 mt-1" />
                    <div>
                      <p className="text-stone-500 text-sm">Phone</p>
                      <p className="text-white">{order.customerPhone || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-4 h-4 text-stone-500 mt-1" />
                    <div>
                      <p className="text-stone-500 text-sm">Preferred Date/Time</p>
                      <p className="text-white">{order.preferredDateTime || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FileText className="w-4 h-4 text-stone-500 mt-1" />
                    <div>
                      <p className="text-stone-500 text-sm">Notes</p>
                      <p className="text-white">{order.notes || 'None'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fulfillment */}
            <Card className="bg-[#2a2a2e] border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Truck className="w-5 h-5 text-rose-400" />
                  Fulfillment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Badge className={order.fulfillmentMethod === 'DELIVERY' ? 'bg-blue-500/20 text-blue-300' : 'bg-green-500/20 text-green-300'}>
                    {order.fulfillmentMethod === 'DELIVERY' ? 'Delivery' : 'Pickup'}
                  </Badge>
                </div>
                {order.fulfillmentMethod === 'DELIVERY' && (
                  <>
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-stone-500 mt-1" />
                      <div>
                        <p className="text-stone-500 text-sm">Delivery Address</p>
                        <p className="text-white">{order.deliveryAddress || 'Not provided'}</p>
                      </div>
                    </div>
                    {order.distanceMiles && (
                      <div>
                        <p className="text-stone-500 text-sm">Distance</p>
                        <p className="text-white">{order.distanceMiles.toFixed(1)} miles</p>
                      </div>
                    )}
                    {order.pricingBreakdown?.deliveryNote && (
                      <p className="text-stone-500 text-sm italic">
                        {order.pricingBreakdown.deliveryNote}
                      </p>
                    )}
                  </>
                )}
                {order.fulfillmentMethod === 'PICKUP' && (
                  <p className="text-stone-400">Pickup from Glendale location</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Pricing */}
            <Card className="bg-[#2a2a2e] border-stone-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-rose-400" />
                  Pricing
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-stone-400">Bouquet</span>
                  <span className="text-white">${order.pricingBreakdown?.baseBouquetPrice || 0}</span>
                </div>
                {order.pricingBreakdown?.wrapPrice > 0 && (
                  <div className="flex justify-between">
                    <span className="text-stone-400">Premium Wrap</span>
                    <span className="text-white">+${order.pricingBreakdown.wrapPrice}</span>
                  </div>
                )}
                {order.fulfillmentMethod === 'DELIVERY' && (
                  <div className="flex justify-between">
                    <span className="text-stone-400">Delivery</span>
                    <span className="text-white">+${order.pricingBreakdown?.deliveryPrice || 0}</span>
                  </div>
                )}
                <div className="border-t border-stone-700 pt-3 flex justify-between">
                  <span className="text-white font-semibold">Total</span>
                  <span className="text-rose-400 font-semibold text-xl">
                    ${order.pricingBreakdown?.totalEstimated || 0}
                  </span>
                </div>
                <div className="text-stone-500 text-sm">
                  Payment: {order.paymentMethod === 'NONE' ? 'Pending' : order.paymentMethod.replace('_', ' ')}
                </div>
              </CardContent>
            </Card>

            {/* Update Status */}
            <Card className="bg-[#2a2a2e] border-stone-700">
              <CardHeader>
                <CardTitle className="text-white">Update Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select
                  value={order.status}
                  onValueChange={handleStatusChange}
                  disabled={updating}
                >
                  <SelectTrigger className="bg-stone-800 border-stone-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-stone-600">
                    <SelectItem value="NEW" className="text-white hover:bg-stone-700">New</SelectItem>
                    <SelectItem value="CONFIRMED" className="text-white hover:bg-stone-700">Confirmed</SelectItem>
                    <SelectItem value="PAID" className="text-white hover:bg-stone-700">Paid</SelectItem>
                    <SelectItem value="DELIVERED" className="text-white hover:bg-stone-700">Delivered</SelectItem>
                    <SelectItem value="CANCELLED" className="text-white hover:bg-stone-700">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                {updating && (
                  <p className="text-stone-500 text-sm flex items-center gap-2">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Updating...
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Timestamps */}
            <Card className="bg-[#2a2a2e] border-stone-700">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 text-stone-500 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Created: {formatDate(order.createdAt)}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-500 text-sm">
                  <RefreshCw className="w-4 h-4" />
                  <span>Updated: {formatDate(order.updatedAt)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminOrderDetail;
